import React, { useState } from 'react';
import {
  Box, Button, Typography, Chip, Slider, CircularProgress, Alert,
} from '@mui/material';
import CheckRoundedIcon from '@mui/icons-material/CheckRounded';
import ArrowForwardRoundedIcon from '@mui/icons-material/ArrowForwardRounded';
import ArrowBackRoundedIcon from '@mui/icons-material/ArrowBackRounded';
import TuneRoundedIcon from '@mui/icons-material/TuneRounded';
import SchoolRoundedIcon from '@mui/icons-material/SchoolRounded';
import StarRoundedIcon from '@mui/icons-material/StarRounded';

const CONTENT_TYPE_COLORS = {
  boeken: '#dbeafe',
  podcasts: '#fce7f3',
  blogs: '#dcfce7',
  frameworks: '#fef3c7',
  keynotes: '#ede9fe',
  artikelen: '#e0e7ff',
  nieuwsbrieven: '#fef9c3',
  onderzoek: '#cffafe',
};

export default function Phase2ExpertDiscovery({
  experts,
  aanbeveling,
  gptRichting,
  loading,
  loadingMessage,
  error,
  onConfirm,
  onBack,
}) {
  const [selectedIds, setSelectedIds] = useState(() =>
    experts ? experts.map((e) => e.id) : []
  );

  React.useEffect(() => {
    if (experts) {
      setSelectedIds(experts.map((e) => e.id));
    }
  }, [experts]);

  const toggleExpert = (id) => {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  const selectedExperts = experts?.filter((e) => selectedIds.includes(e.id)) || [];

  if (loading) {
    return (
      <div className="gpt-loading">
        <CircularProgress size={48} />
        <div className="gpt-loading-text">
          <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
            Experts analyseren...
          </Typography>
          {loadingMessage && (
            <Typography variant="body2" sx={{ color: '#6366f1', fontWeight: 600, mb: 1 }}>
              {loadingMessage}
            </Typography>
          )}
          <Typography variant="body2" color="text.secondary">
            De AI Engine kan even nodig hebben om op te starten.
            <br />Dit kan tot 60 seconden duren.
          </Typography>
        </div>
        <div className="gpt-loading-dots">
          <span>•</span><span>•</span><span>•</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="gpt-phase">
        <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>
        <Button startIcon={<ArrowBackRoundedIcon />} onClick={onBack}>
          Terug
        </Button>
      </div>
    );
  }

  if (!experts || experts.length === 0) return null;

  return (
    <div className="gpt-phase">
      <div className="gpt-phase-header">
        <Typography variant="h5" sx={{ fontWeight: 700, mb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
          <SchoolRoundedIcon sx={{ color: '#6366f1' }} />
          Gevonden experts & thought leaders
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {aanbeveling}
        </Typography>
      </div>

      {/* GPT Direction */}
      {gptRichting && (
        <Alert severity="info" sx={{ mb: 3 }} icon={<TuneRoundedIcon />}>
          <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>Aanbevolen GPT-richting</Typography>
          <Typography variant="body2">{gptRichting}</Typography>
        </Alert>
      )}

      {/* Expert Cards */}
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2, mb: 3 }}>
        {experts.map((expert) => {
          const isSelected = selectedIds.includes(expert.id);
          return (
            <div
              key={expert.id}
              className={`expert-card ${isSelected ? 'selected' : ''}`}
              onClick={() => toggleExpert(expert.id)}
            >
              <div className="expert-card-check">
                {isSelected && <CheckRoundedIcon sx={{ fontSize: 14, color: '#fff' }} />}
              </div>

              {/* Header */}
              <Box sx={{ mb: 1.5, pr: 4 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 700, lineHeight: 1.3 }}>
                  {expert.naam}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {expert.titel}
                </Typography>
              </Box>

              {/* Relevance Score */}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1.5 }}>
                {[...Array(5)].map((_, i) => (
                  <StarRoundedIcon
                    key={i}
                    sx={{
                      fontSize: 14,
                      color: i < Math.round((expert.relevantieScore || 7) / 2)
                        ? '#f59e0b' : '#e5e7eb',
                    }}
                  />
                ))}
                <Typography variant="caption" color="text.secondary" sx={{ ml: 0.5 }}>
                  {expert.relevantieScore}/10
                </Typography>
              </Box>

              {/* Why relevant */}
              <Typography variant="body2" sx={{ mb: 1, color: '#374151', lineHeight: 1.5 }}>
                {expert.waaromRelevant}
              </Typography>

              {/* Strengths */}
              <Typography variant="caption" sx={{ fontWeight: 600, color: '#6366f1', display: 'block', mb: 0.5 }}>
                Sterk in:
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1, fontSize: '0.8rem' }}>
                {expert.stpierkten || expert.sterkten}
              </Typography>

              {/* Unique angle */}
              <Typography variant="caption" sx={{ fontWeight: 600, color: '#0d9488', display: 'block', mb: 0.5 }}>
                Unieke invalshoek:
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5, fontSize: '0.8rem' }}>
                {expert.uniekeInvalshoek}
              </Typography>

              {/* Known work */}
              {expert.bekendWerk && (
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1, fontStyle: 'italic' }}>
                  "{expert.bekendWerk}"
                </Typography>
              )}

              {/* Content types */}
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                {(expert.contentTypes || []).map((type, i) => (
                  <Chip
                    key={i}
                    label={type}
                    size="small"
                    sx={{
                      fontSize: '0.65rem',
                      height: 22,
                      backgroundColor: CONTENT_TYPE_COLORS[type.toLowerCase()] || '#f3f4f6',
                      color: '#374151',
                      fontWeight: 600,
                    }}
                  />
                ))}
              </Box>
            </div>
          );
        })}
      </Box>

      {/* Selection summary */}
      <Box sx={{
        p: 2,
        background: '#f9fafb',
        borderRadius: 3,
        border: '1px solid #e5e7eb',
        mb: 3,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
      }}>
        <Typography variant="body2" color="text.secondary">
          <strong>{selectedIds.length}</strong> van {experts.length} experts geselecteerd
        </Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            size="small"
            onClick={() => setSelectedIds(experts.map((e) => e.id))}
          >
            Alles selecteren
          </Button>
          <Button
            size="small"
            onClick={() => setSelectedIds([])}
          >
            Alles deselecteren
          </Button>
        </Box>
      </Box>

      {/* Actions */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
        <Button
          startIcon={<ArrowBackRoundedIcon />}
          onClick={onBack}
        >
          Terug
        </Button>
        <Button
          variant="contained"
          size="large"
          endIcon={<ArrowForwardRoundedIcon />}
          onClick={() => onConfirm(selectedExperts)}
          disabled={selectedExperts.length === 0}
          sx={{ px: 4 }}
        >
          GPT-profiel bouwen
        </Button>
      </Box>
    </div>
  );
}
